/**
 * Putting the reasons an invoice cannot be reopened next to the fields that
 * cause them.
 *
 * `POST /invoices/:id/reopen` answers 400 with `details`: the full list of what
 * the FatturaPA generator and validator refuse about the document, already
 * written in Italian for whoever is looking at the screen. That list is the
 * only thing standing between a scartata invoice and the queue, and until now
 * it was shown as one block of prose above a form with nine fields — the
 * operator had to work out for themselves that «la rata 2 del piano di
 * pagamento non indica la modalità di pagamento» is about the payment section
 * and not about the lines.
 *
 * The API sends the sentences, not the codes behind them (see
 * `localizeIssues()` in src/i18n/index.js), so the classification here reads
 * the sentences. The patterns are deliberately anchored on the nouns the
 * catalog uses for the *thing* — «piano di pagamento», «riepilogo IVA»,
 * «totale della fattura» — and never on wording that could be reworded without
 * changing which field is at fault. Anything that matches nothing at all is
 * still shown, at the top of the form: an issue the operator cannot see is far
 * worse than one filed under the wrong heading.
 *
 * Also holds `buildCorrectionPagamento()`, the `pagamento` builder
 * `InvoiceCorrectionForm.svelte` saves with. It lived inside that `.svelte`
 * file until nothing could `node:test`-import it and exercise the one rule
 * that matters — that an untouched save must not flatten a stored plan or
 * drop a field the form does not show. It moved here, unit-tested and Svelte-
 * free, for the same reason `classifyCorrectionIssues()` is.
 *
 * Kept free of Svelte imports so both can be unit-tested directly.
 */

/** Correction fields an issue can be filed under, in the order the form shows them. */
export const CORRECTION_FIELDS = [
  'invoiceDate', 'tipoDocumento', 'causale', 'esigibilitaIVA',
  'codiceDestinatario', 'pecDestinatario', 'lineItems', 'totalAmount', 'pagamento',
];

/**
 * First match wins, so the order is the specificity order:
 *
 *  - the payment plan is named by its own words and by nothing else;
 *  - the collectability sentence says «esigibilità» and nothing else does;
 *  - «totale della fattura … mentre le righe» also mentions the rows, so the
 *    total has to be tested before them or it would be filed under the lines;
 *  - the row-level rules come last among the money ones, because they are the
 *    broadest.
 */
const RULES = [
  { field: 'pagamento', pattern: /piano di pagamento|modalit[àa] di pagamento|\brat[ae]\b/i },
  { field: 'esigibilitaIVA', pattern: /esigibilit/i },
  { field: 'totalAmount', pattern: /totale (?:della fattura|del documento)/i },
  { field: 'lineItems', pattern: /\brigh[ae]\b|riepilogo iva|aliquota|imponibile|imposta|natura|quantit[àa]|prezzo unitario|niente da fatturare/i },
  { field: 'invoiceDate', pattern: /data della fattura|manca la data/i },
  { field: 'tipoDocumento', pattern: /tipo di documento|che tipo di documento/i },
  { field: 'codiceDestinatario', pattern: /codice destinatario/i },
  { field: 'pecDestinatario', pattern: /\bpec\b/i },
];

/** «La riga 3 indica…» → 3. Null when the sentence names no row. */
function rowNumber(text) {
  const m = /\brig[ah]\s+(\d+)/i.exec(text);
  return m ? Number(m[1]) : null;
}

/** «La rata 2 del piano…» → 2. Null when the sentence names no instalment. */
function instalmentNumber(text) {
  const m = /\brata\s+(\d+)/i.exec(text);
  return m ? Number(m[1]) : null;
}

function fieldFor(text) {
  return RULES.find((rule) => rule.pattern.test(text))?.field || null;
}

/** '' and whitespace-only both mean "not stated" — never the literal string. */
export function blankToNull(value) {
  const text = value == null ? '' : String(value).trim();
  return text === '' ? null : text;
}

/**
 * The `pagamento` block `InvoiceCorrectionForm.svelte` saves: the loaded one
 * with the edited fields written over it, so nothing this form does not show
 * is lost. Returns null when there is no payment plan and the operator wrote
 * none.
 *
 * Deliberately **not** `lib/payment.js`'s `buildPagamento()`, and must not be
 * folded into it: this is the one screen that has to let the operator edit
 * `dettagli` — it exists to fix the very instalment SdI just refused — while
 * the shared builder refuses to touch a plan in instalments at all and hands
 * one back untouched. Giving the shared builder an instalments-editable mode
 * would put that capability on every other screen that calls it.
 *
 * @param {object} edited - `form.pagamento`, the editor's own fields
 * @param {object|null} storedPagamento - the block exactly as loaded, kept
 *   verbatim so a field this form does not show — ABI, BIC, the beneficiary —
 *   survives a correction instead of being dropped by it
 * @returns {object|null}
 */
export function buildCorrectionPagamento(edited, storedPagamento) {
  const dettagli = edited.dettagli.map((d, i) => {
    const stored = storedPagamento?.dettagli?.[i] || {};
    return {
      ...stored,
      modalitaPagamento: blankToNull(d.modalitaPagamento) || undefined,
      importoPagamento: blankToNull(d.importoPagamento) || undefined,
      dataScadenza: blankToNull(d.dataScadenza) || undefined,
    };
  });
  const merged = {
    ...(storedPagamento || {}),
    condizioni: blankToNull(edited.condizioni) || undefined,
    modalitaPagamento: blankToNull(edited.modalitaPagamento) || undefined,
    importoPagamento: blankToNull(edited.importoPagamento) || undefined,
    dataScadenza: blankToNull(edited.dataScadenza) || undefined,
    giorniTerminiPagamento: blankToNull(edited.giorniTerminiPagamento) || undefined,
    iban: blankToNull(edited.iban) || undefined,
  };
  if (dettagli.length) merged.dettagli = dettagli;
  else delete merged.dettagli;
  const hasSomething = Object.values(merged).some((v) => v !== undefined && v !== null);
  return hasSomething ? merged : null;
}

/**
 * Sort the issues blocking a reopen into the places the form can show them.
 *
 * @param {Array<string>|null|undefined} details - the `details` of the 400
 * @returns {{
 *   all: string[],
 *   fields: Record<string, string[]>,
 *   lines: Record<number, string[]>,
 *   instalments: Record<number, string[]>,
 *   general: string[],
 * }} `lines` and `instalments` are keyed by zero-based index, so they can be
 *   read straight off an `{#each}`; `general` is what matched nothing and is
 *   shown whole, at the top.
 */
export function classifyCorrectionIssues(details) {
  const all = (Array.isArray(details) ? details : [])
    .map((d) => (typeof d === 'string' ? d.trim() : String(d?.message || '').trim()))
    .filter(Boolean);

  const fields = {};
  const lines = {};
  const instalments = {};
  const general = [];

  for (const text of all) {
    const field = fieldFor(text);
    if (field === 'pagamento') {
      const rata = instalmentNumber(text);
      if (rata) {
        (instalments[rata - 1] ||= []).push(text);
        continue;
      }
    }
    if (field === 'lineItems' || field === 'totalAmount') {
      const riga = rowNumber(text);
      if (riga) {
        (lines[riga - 1] ||= []).push(text);
        continue;
      }
    }
    if (!field) {
      general.push(text);
      continue;
    }
    (fields[field] ||= []).push(text);
  }

  return { all, fields, lines, instalments, general };
}
